// ---------- player preferences ----------
// The switches of the settings dialog, plus `flood`, which no switch drives for now. Deliberately
// kept apart from S: those are game
// state, rebuilt on every puzzle, while these outlive it and have to be written to disk. The
// module owns reading, writing and APPLYING them — a caller flips a switch, it doesn't have to
// know that sound lives on S and motion lives in the animation engine.
//
// Not synced to the cloud, and that is on purpose: a preference belongs to the device it was
// set on. Somebody who silences the game on the phone in a waiting room has said nothing about
// the tablet at home.

import { SYSTEM_REDUCED_MOTION, setMotion } from './anim';
import { DEFAULT_INK, INKS, setInk } from '../shared/ink';
import {
  DEFAULT_MARK_HUE,
  DEFAULT_MARK_STYLE,
  MARK_HUES,
  MARK_STYLES,
  setMarkHue,
  setMarkStyle,
} from './mark';
import { resetPal } from './palette';
import { applyChrome } from './platform';
import { DEFAULT_SKIN, SKINS, skinById, type SkinId } from './skin';
import { S } from './state';

const KEY = 'codigrid:settings:v1';

export interface Settings {
  sound: boolean;
  haptics: boolean;
  /** Animations ON. Starts from the system's "reduced motion", which the player may override
   *  in either direction — the OS setting is a starting point, not a verdict. */
  motion: boolean;
  /** Zen: aiming at a cell of another symbol costs strictly nothing (see paintAt()) — and,
   *  the same forgiveness read the other way round, a near miss is credited to the nearest
   *  cell of the locked symbol instead of falling on the floor (see toleratedCell()).
   *
   *  Plus aucun interrupteur ne l'expose, et pour la même raison que `flood` juste en dessous :
   *  c'est la règle du jeu et non une préférence. Il n'a jamais été qu'à `true` en pratique, et
   *  proposer de le couper revenait à proposer une punition à qui vient colorier. Le champ reste
   *  pour que la sanction — le heurt, la secousse, le combo perdu — soit à une ligne de revenir,
   *  et n'est donc plus relu du disque : voir loadSettings(). */
  zen: boolean;
  /** La boussole du bord d'écran : la flèche qui pointe vers le symbole verrouillé quand plus
   *  aucune de ses cases n'est en vue (cf. findGuide/drawGuide dans render.ts).
   *
   *  Allumée par défaut, parce que c'est une aide et non une assistance : elle ne révèle rien
   *  du dessin — elle dit seulement de quel côté regarder, une information que dézoomer donne
   *  aussi, en plus lentement. Un joueur qui préfère chercher lui-même la coupe. */
  compass: boolean;
  /** Filling a cell carries on through its whole contiguous zone of the same symbol instead of
   *  stopping at the cell touched. Applies to a tap and to a swept stroke alike — see paintAt()
   *  and paintStrokeAt().
   *
   *  No switch exposes it at the moment: it is the rule of the game, not a preference, and it
   *  stays a field only so the one-cell behaviour is a line away from coming back. Hence it is
   *  never read back from disk — see loadSettings(). */
  flood: boolean;
  /** L'encre dont les cases se remplissent, par son id (cf. INKS). Une chaîne et non une
   *  couleur : ce qui est enregistré doit survivre à un changement de nuance. */
  ink: string;
  /** Le repère des cases à faire : sa forme et sa teinte, par leurs ids (cf. src/mark.ts).
   *  Deux réglages et non un seul, parce qu'ils ne répondent pas à la même gêne — la forme dit
   *  combien le repère couvre le dessin, la teinte dit combien il s'en détache. */
  markStyle: string;
  markHue: string;
  /** L'habillage de l'interface — « Clair », « Ciel » ou « Soir », cf. src/skin.ts. Un
   *  identifiant et non des couleurs : la charte reste dans la feuille de style, et ce champ
   *  ne fait que désigner lequel de ses trois blocs [data-skin] s'applique.
   *
   *  Locale à l'appareil comme tout ce fichier, et ici la raison est plus forte qu'ailleurs :
   *  on choisit un thème pour l'écran qu'on a sous les yeux et la lumière de la pièce, pas
   *  pour un compte. Quelqu'un qui joue en « Soir » dans son lit n'a rien dit de sa tablette. */
  skin: SkinId;
  /** Notifications push (Android seulement, cf. src/push.ts). Reste local à l'appareil comme le
   *  reste de ce fichier — un token FCM est spécifique à l'appareil, une préférence qui viendrait
   *  d'ailleurs n'aurait aucun sens ici. Pas dans ROWS (src/prefs.ts) : l'activer demande une
   *  permission système et un aller-retour réseau, pas un simple flip. */
  pushEnabled: boolean;
}

/** Sound stays off by default: a game that starts making noise in a quiet room is a game people
 *  close. Haptics are off too, for what turns out to be the same reason applied to the hand
 *  instead of the room. The argument for leaving them on was that a vibration is felt by nobody
 *  but the player — true, and beside the point: with zone filling ON (see below), a single touch
 *  takes dozens of cells, so the phone hums continuously rather than ticking once per gesture. A
 *  sheet opened to unwind should not buzz in the palm, and whoever wants the feedback will find
 *  the switch — the same bargain sound already makes.
 *
 *  Zen is ON, so the punishment is what you opt into. A misaim is the ordinary event here — cells
 *  are a few pixels wide and the codes are read at a glance — and answering it with a dull thud
 *  turns a colouring sheet into an exam. Whoever wants the stakes back will go and ask for them;
 *  whoever just wants to colour never has to find the switch.
 *
 *  Zone filling is ON: one touch takes the whole contiguous zone of the same symbol. What the
 *  player is doing is colouring a sheet, and on a 200-wide grid a zone is dozens of cells — asking
 *  for each of them, or for a stroke steady enough to sweep them, is asking for patience rather
 *  than for reading. The sweep keeps its point on top of it: it chains ZONES now. */
const DEFAULTS: Settings = {
  sound: false,
  haptics: false,
  motion: !SYSTEM_REDUCED_MOTION,
  zen: true,
  compass: true,
  flood: true,
  ink: DEFAULT_INK,
  markStyle: DEFAULT_MARK_STYLE,
  markHue: DEFAULT_MARK_HUE,
  skin: DEFAULT_SKIN,
  pushEnabled: false,
};

let current: Settings = { ...DEFAULTS };

export function settings(): Readonly<Settings> {
  return current;
}

/** Pushes the values where the rest of the code reads them. */
function apply(): void {
  S.sound = current.sound;
  S.haptics = current.haptics;
  setMotion(current.motion);
  setInk(current.ink);
  setMarkStyle(current.markStyle);
  setMarkHue(current.markHue);
  // La teinte quitte la feuille : l'interface la porte aussi — le bord du symbole verrouillé et
  // sa barre, les filtres du catalogue et son menu du bas. Le CSS fait la correspondance
  // teinte → couleurs (cf. les blocs [data-hue] de style.css), donc il n'y a qu'un identifiant
  // à poser ici, et ajouter une quatrième teinte ne touchera pas cette ligne.
  document.documentElement.dataset['hue'] = current.markHue;
  // Le thème, même mécanique que la teinte juste au-dessus : un identifiant posé sur <html>,
  // et les blocs [data-skin] de style.css font la correspondance. Le script en tête de
  // index.html pose DÉJÀ cet attribut avant la première image — celui-ci ne compte donc que
  // pour les changements en cours de session, mais les deux doivent écrire la même chose.
  document.documentElement.dataset['skin'] = current.skin;
  // La couleur que le système peint autour de la page. Elle est dans le balisage pour le
  // premier rendu ; la voici tenue à jour quand le joueur change de thème.
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = skinById(current.skin).chrome;
  // Et la barre système du natif, quand elle est rappelée par un glissement depuis le haut
  // (elle est cachée le reste du temps — cf. initPlatform). Sur le web, sans effet.
  void applyChrome();
  // La teinte du repère est cuite dans les couleurs composées une fois pour toutes : sans ce
  // rejet, en changer ne repeindrait rien avant le prochain rechargement.
  resetPal();
}

/** Guarded like the progress store: a full quota or a private-mode browser costs the
 *  preferences, never the session. */
function write(): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(current));
  } catch {
    /* play on, unremembered */
  }
}

/** Call once at startup, before anything reads S.sound or animates. */
export function loadSettings(): void {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<Settings> | null;
    // Field by field: a payload from an older build is missing keys, and a newer one may carry
    // some this build knows nothing about.
    // `flood` ET `zen` sont absents de la liste à dessein : plus rien ne permet de les régler,
    // donc un `false` écrit par une version qui le permettait serait un interrupteur bloqué en
    // position basse, sans aucun moyen de le rouvrir. C'est très exactement ce qui arriverait
    // aux joueurs qui ont coupé le mode zen avant cette version.
    if (raw && typeof raw === 'object') {
      for (const k of ['sound', 'haptics', 'motion', 'compass', 'pushEnabled'] as const) {
        if (typeof raw[k] === 'boolean') current[k] = raw[k];
      }
      // Validée contre la liste : une couleur retirée d'une version à l'autre ne doit pas
      // laisser la feuille sans encre.
      if (typeof raw.ink === 'string' && INKS.some((i) => i.id === raw.ink)) current.ink = raw.ink;
      // Mêmes précautions : un style ou une teinte retirés d'une version à l'autre laisseraient
      // le joueur avec un repère que plus rien ne sait dessiner.
      if (typeof raw.markStyle === 'string' && MARK_STYLES.some((m) => m.id === raw.markStyle))
        current.markStyle = raw.markStyle;
      if (typeof raw.markHue === 'string' && MARK_HUES.some((h) => h.id === raw.markHue))
        current.markHue = raw.markHue;
      // Même précaution : un thème retiré d'une version à l'autre laisserait l'attribut posé
      // sur une valeur qu'aucun bloc [data-skin] ne reconnaît, donc l'écran sur le défaut de
      // :root — soit un thème que le joueur n'a pas choisi et qu'aucune pastille ne montre
      // comme actif.
      if (typeof raw.skin === 'string' && SKINS.some((k) => k.id === raw.skin))
        current.skin = raw.skin as SkinId;
    }
  } catch {
    current = { ...DEFAULTS };
  }
  apply();
}

/** Every change goes through here: applied, saved, and echoed to whoever displays it — the
 *  dialog and the panel's own sound toggle show the same truth. */
export function setSetting<K extends keyof Settings>(key: K, value: Settings[K]): void {
  current[key] = value;
  apply();
  write();
  for (const cb of listeners) cb();
}

// Several places display the same switch (the dialog, and the Son button in the game panel):
// a list rather than one callback, so neither has to know the other exists.
const listeners: Array<() => void> = [];

export function wireSettings(cb: () => void): void {
  listeners.push(cb);
  cb(); // paint once from whatever was loaded
}
